import api from '@/utils/axios';
import { getSession } from '@/domains/auth/services/auth.service';
import { getMe } from '@/domains/auth/services/auth.service';
import { updateUser } from '@/domains/api/users.api';
import { ORDER_API_AVAILABLE } from '@/domains/api';
import {
  listOrderRequests,
  getOrderRequest,
  createOrderRequest,
  confirmOrderPayment,
  type OrderRequestPayload,
  type ConfirmPaymentPayload,
} from '@/domains/api/delivery.api';
import {
  fetchClientDashboard as apiFetchClientDashboard,
  fetchClientWallet as apiFetchClientWallet,
  fetchCommunityTrips as apiFetchCommunityTrips,
  fetchClientNotifications as apiFetchClientNotifications,
  markClientNotificationRead as apiMarkClientNotificationRead,
  markAllClientNotificationsRead as apiMarkAllClientNotificationsRead,
  depositClientWallet as apiDepositClientWallet,
  withdrawClientWallet as apiWithdrawClientWallet,
  type WalletOperationPayload,
  type ClientDashboardData,
  type ClientWalletData,
  type CommunityTrip,
  type ClientOrderSummary,
  type WalletTransaction,
  type ClientNotification,
} from '@/domains/api/client.api';
import type { ApiOrderRequest } from '@/domains/api/types';

export interface UserProfile {
  id: number;
  firstnames: string;
  lastname: string;
  email: string | null;
  phone: string;
  status: string;
  zone?: { id: number; name: string };
}

export interface Order {
  id: number;
  code_suivi: string;
  statut: string;
  type_livraison?: string;
  adresse_ramassage: string;
  adresse_livraison: string;
  prix_estime?: number;
  distance_km?: number;
  created_at: string;
  payment_status?: string | null;
  payment_timing?: string | null;
  payment_method?: string;
  payment_reference?: string | null;
  paid_at?: string | null;
  payer_phone?: string | null;
  mobile_money_provider?: string | null;
  departure_zone?: string;
  arrival_zone?: string;
  livraisons?: Array<{
    id: number;
    statut: string;
    livreur?: { id: number; firstnames: string; lastname: string; phone: string };
  }>;
}

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : parseFloat(value) || 0;
}

function mapOrderRequest(raw: ApiOrderRequest): Order {
  const driver = raw.driver;
  return {
    id: raw.id,
    code_suivi: `CMD-${raw.id}`,
    statut: raw.status,
    type_livraison: raw.delivery_type?.name,
    adresse_ramassage: [raw.departure_zone?.name, raw.departure_address].filter(Boolean).join(' — '),
    adresse_livraison: [raw.arrival_zone?.name, raw.arrival_address].filter(Boolean).join(' — '),
    prix_estime: toNumber(raw.total_delivery_fee),
    created_at: raw.created_at ?? new Date().toISOString(),
    payment_status: raw.payment_status ?? null,
    payment_timing: raw.payment_timing,
    payment_method: raw.payment_method?.name,
    payment_reference: raw.payment_reference,
    paid_at: raw.paid_at,
    payer_phone: raw.payer_phone,
    mobile_money_provider: raw.mobile_money_provider,
    departure_zone: raw.departure_zone?.name,
    arrival_zone: raw.arrival_zone?.name,
    livraisons: driver
      ? [{
          id: raw.id,
          statut: raw.status,
          livreur: {
            id: driver.id,
            firstnames: driver.firstnames,
            lastname: driver.lastname,
            phone: driver.phone,
          },
        }]
      : undefined,
  };
}

export interface Address {
  id: number;
  user_id: number;
  nom_adresse: string;
  adresse_complete: string;
  est_principale: boolean;
  point_repere?: string;
  quartier?: string;
  ville?: string;
  pays?: string;
  code_postal?: string;
}

export interface Notification {
  id: string;
  titre: string;
  message: string;
  est_lu: boolean;
  created_at: string;
  type?: string;
  reference_id?: number;
}

export interface ClientSettings {
  notifySms: boolean;
  notifyEmail: boolean;
  notifyPush: boolean;
  newsletter: boolean;
  language: 'fr' | 'en';
}

const CLIENT_SETTINGS_KEY = 'onapila_client_settings';

const DEFAULT_CLIENT_SETTINGS: ClientSettings = {
  notifySms: true,
  notifyEmail: true,
  notifyPush: true,
  newsletter: false,
  language: 'fr',
};

export function loadClientSettings(): ClientSettings {
  try {
    const raw = localStorage.getItem(CLIENT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_CLIENT_SETTINGS };
    return { ...DEFAULT_CLIENT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_CLIENT_SETTINGS };
  }
}

export function saveClientSettings(settings: ClientSettings): void {
  localStorage.setItem(CLIENT_SETTINGS_KEY, JSON.stringify(settings));
}

function mapProfile(raw: Record<string, unknown>): UserProfile {
  return {
    id: raw.id as number,
    firstnames: raw.firstnames as string,
    lastname: raw.lastname as string,
    email: (raw.email as string | null) ?? null,
    phone: raw.phone as string,
    status: raw.status as string,
    zone: raw.zone as UserProfile['zone'],
  };
}

export async function fetchUserProfile(): Promise<UserProfile> {
  const user = await getMe();
  return mapProfile(user as unknown as Record<string, unknown>);
}

export async function updateUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
  const session = getSession();
  const userId = session?.user?.id;
  if (!userId) throw new Error('Session utilisateur introuvable');

  const current = await fetchUserProfile();
  const merged = { ...current, ...data };

  const updated = await updateUser(userId, {
    firstnames: merged.firstnames,
    lastname: merged.lastname,
    phone: merged.phone,
    email: merged.email,
    status: merged.status,
    zone_id: merged.zone?.id ?? current.zone?.id ?? 1,
  });

  return mapProfile(updated as unknown as Record<string, unknown>);
}

// ── Dashboard client ──────────────────────────────────────────
export async function fetchClientDashboard(): Promise<ClientDashboardData> {
  return apiFetchClientDashboard();
}

export async function fetchClientWallet(): Promise<ClientWalletData> {
  return apiFetchClientWallet();
}

export async function fetchCommunityTrips(): Promise<CommunityTrip[]> {
  return apiFetchCommunityTrips();
}

export async function depositWallet(payload: WalletOperationPayload): Promise<ClientWalletData> {
  return apiDepositClientWallet(payload);
}

export async function withdrawWallet(payload: WalletOperationPayload): Promise<ClientWalletData> {
  return apiWithdrawClientWallet(payload);
}

export type { WalletOperationPayload };

export type { ClientDashboardData, ClientWalletData, CommunityTrip, ClientOrderSummary, WalletTransaction };

// ── Commandes (order-requests) ────────────────────────────────
export async function fetchClientOrders(): Promise<Order[]> {
  if (!ORDER_API_AVAILABLE) return [];
  const session = getSession();
  const userId = session?.user?.id;
  const all = await listOrderRequests();
  const mine = userId ? all.filter((o) => o.created_by_id === userId) : all;
  return mine.map(mapOrderRequest);
}

export async function fetchOrderDetail(orderId: number): Promise<Order> {
  if (!ORDER_API_AVAILABLE) throw new Error('API commandes indisponible');
  const raw = await getOrderRequest(orderId);
  return mapOrderRequest(raw);
}

export async function createOrder(data: OrderRequestPayload): Promise<Order> {
  const raw = await createOrderRequest(data);
  return mapOrderRequest(raw);
}

export async function payOrder(orderId: number, payload: ConfirmPaymentPayload): Promise<Order> {
  const raw = await confirmOrderPayment(orderId, payload);
  return mapOrderRequest(raw);
}

export type { OrderRequestPayload, ConfirmPaymentPayload };

export async function fetchClientAddresses(): Promise<Address[]> {
  const res = await api.get('/addresses');
  const data = res.data.data ?? res.data ?? [];
  return Array.isArray(data) ? data : [];
}

export async function createAddress(data: Partial<Address>): Promise<Address> {
  const res = await api.post('/addresses', data);
  return res.data.data ?? res.data;
}

export async function updateAddress(id: number, data: Partial<Address>): Promise<Address> {
  const res = await api.patch(`/addresses/${id}`, data);
  return res.data.data ?? res.data;
}

export async function deleteAddress(id: number): Promise<void> {
  await api.delete(`/addresses/${id}`);
}

export async function fetchNotifications(): Promise<Notification[]> {
  return apiFetchClientNotifications();
}

export async function markNotificationAsRead(id: string): Promise<void> {
  await apiMarkClientNotificationRead(id);
}

export async function markAllNotificationsAsRead(): Promise<void> {
  await apiMarkAllClientNotificationsRead();
}

export type { ClientNotification };

export async function uploadProfilePhoto(_file: File): Promise<{ photo_url: string }> {
  throw new Error('Upload photo : endpoint non disponible côté backend');
}
